import { useTranslation } from '@govard/i18n-core/react';
import { Alert, Button, Card, Checkbox, Input, Modal, Select, Typography } from 'antd';
import type { ReactElement } from 'react';
import { useState } from 'react';

import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import {
  workspaceDeleteRefusalReason,
  type WorkspaceExportFormat,
} from '../api/workspace-data.client.js';
import {
  useWorkspaceDataDelete,
  useWorkspaceDataExportDownload,
} from '../hooks/useWorkspaceData.js';

const { Text, Title } = Typography;

/** Machine-readable backend refusals → localized messages. */
function deleteErrorKey(err: unknown): string {
  switch (workspaceDeleteRefusalReason(err)) {
    case 'personal_workspace':
      return 'crm.settings.data.error.personal_workspace';
    case 'last_workspace':
      return 'crm.settings.data.error.last_workspace';
    case 'invalid_password':
      return 'crm.settings.data.error.invalid_password';
    case 'password_not_set':
      return 'crm.settings.data.error.password_not_set';
    default:
      return 'crm.settings.data.error.generic';
  }
}

/**
 * GDPR self-service data panel (audit P1-8): export every workspace record in
 * one download, or delete the whole workspace from a password-confirmed
 * danger zone. Backend enforces the roles; the UI mirrors them.
 */
export function WorkspaceDataPanel(): ReactElement {
  const { t } = useTranslation();
  const scope = useCurrentScope();
  const exportDownload = useWorkspaceDataExportDownload();
  const deleteMutation = useWorkspaceDataDelete();
  const [format, setFormat] = useState<WorkspaceExportFormat>('json');
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [exportError, setExportError] = useState<unknown>(null);
  const [deleteError, setDeleteError] = useState<unknown>(null);

  // Mirrors the backend gates: export is owner/admin, deletion is owner-only.
  const canExport = scope?.role === 'owner' || scope?.role === 'admin';
  const canDelete = scope?.role === 'owner';

  const closeDeleteModal = (): void => {
    setIsDeleteOpen(false);
    setPassword('');
    setAcknowledged(false);
    setDeleteError(null);
  };

  const runExport = async (): Promise<void> => {
    setExportError(null);
    try {
      const { blob, fileName } = await exportDownload.mutateAsync(format);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err);
    }
  };

  const runDelete = async (): Promise<void> => {
    setDeleteError(null);
    try {
      await deleteMutation.mutateAsync({ password, confirm: true });
      // Every member session — including this one — is revoked server-side,
      // so a full reload is the honest transition: caches go, login page stays.
      window.location.assign('/login');
    } catch (err) {
      setDeleteError(err);
    }
  };

  return (
    <Card aria-label={t('crm.settings.data.title')}>
      <Title level={5}>{t('crm.settings.data.title')}</Title>
      <Text type="secondary">{t('crm.settings.data.description')}</Text>

      <Card size="small" className="mt-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Text strong>{t('crm.settings.data.export_title')}</Text>
            <br />
            <Text type="secondary">{t('crm.settings.data.export_description')}</Text>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              aria-label={t('crm.settings.data.export_format_label')}
              value={format}
              onChange={(next: WorkspaceExportFormat) => setFormat(next)}
              options={[
                { value: 'json', label: t('crm.settings.data.export_format_json') },
                { value: 'csv', label: t('crm.settings.data.export_format_csv') },
              ]}
              style={{ minWidth: 130 }}
            />
            <Button
              loading={exportDownload.isPending}
              disabled={!canExport}
              onClick={() => void runExport()}
            >
              {t('crm.settings.data.export_action')}
            </Button>
          </div>
        </div>
      </Card>

      {canDelete ? (
        <Card size="small" className="mt-4" title={t('crm.settings.data.danger_title')}>
          <Text type="secondary">{t('crm.settings.data.danger_description')}</Text>
          <div className="mt-4">
            <Button danger onClick={() => setIsDeleteOpen(true)}>
              {t('crm.settings.data.delete_action')}
            </Button>
          </div>
        </Card>
      ) : null}

      {exportError !== null ? (
        <Alert className="mt-4" type="error" showIcon title={t('crm.settings.data.export_error')} />
      ) : null}
      {deleteError !== null ? (
        <Alert className="mt-4" type="error" showIcon title={t(deleteErrorKey(deleteError))} />
      ) : null}

      <Modal
        open={isDeleteOpen}
        title={t('crm.settings.data.delete_title')}
        okButtonProps={{
          danger: true,
          disabled: password.length === 0 || !acknowledged,
          loading: deleteMutation.isPending,
        }}
        okText={t('crm.settings.data.delete_confirm')}
        cancelText={t('crm.common.cancel')}
        onCancel={closeDeleteModal}
        onOk={() => void runDelete()}
      >
        <Alert type="warning" showIcon title={t('crm.settings.data.delete_warning')} />
        <Checkbox
          className="mt-4"
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
        >
          {t('crm.settings.data.delete_acknowledge')}
        </Checkbox>
        <Input.Password
          className="mt-3"
          aria-label={t('crm.settings.data.delete_password_label')}
          placeholder={t('crm.settings.data.delete_password_label')}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </Modal>
    </Card>
  );
}
